import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useDateTimeMessages } from '../../forms-core/hooks/useDateTimeMessages.js';
import { getOutsideRangeMessage } from '../../forms-core/time/timeParser.js';
import type { DateTimeRangePickerProps, ValidationError } from '../types.js';

export const useDateTimeRangeErrorMessage = ({
  min,
  max,
  precision,
}: Pick<DateTimeRangePickerProps, 'max' | 'min' | 'precision'>) => {
  const intl = useIntl();
  const { formatString: expectedFormat } = useDateTimeMessages({ precision });
  const getErrorMessage = useCallback(
    ({ error, source }: ValidationError) => {
      if (error === 'invalid') {
        if (source === 'from') {
          return intl.formatMessage(
            {
              defaultMessage:
                'Invalid start time. Expected format: {expectedFormat}, -2h or -2d@d',
              id: 'supuOfI2cSrNKOZ/',
              description: 'Given from value is invalid',
            },
            {
              expectedFormat,
            },
          );
        }

        if (source === 'to') {
          return intl.formatMessage(
            {
              defaultMessage:
                'Invalid end time. Expected format: {expectedFormat}, -2h or -2d@d',
              id: 'HL9dGWGnhYkm7OyC',
              description: 'Given to value is invalid',
            },
            {
              expectedFormat,
            },
          );
        }

        return intl.formatMessage(
          {
            defaultMessage:
              'Invalid start and end time. Expected format: {expectedFormat}, -2h or -2d@d',
            id: '89sBAyzLbeW6GyPJ',
            description: 'Given from and to values are invalid',
          },
          {
            expectedFormat,
          },
        );
      }

      if (error === 'invalidOrder') {
        return intl.formatMessage({
          defaultMessage: 'Start time must be before end time.',
          id: '6mmv2U9zlsEKmabI',
          description: 'Given from must be before given end time',
        });
      }

      if (error === 'outsideRange') {
        return getOutsideRangeMessage(intl, min, max, precision);
      }

      if (error === 'valueMissing') {
        if (source === 'from') {
          return intl.formatMessage({
            defaultMessage: 'Please enter a start time.',
            id: 'GUeetpTpusqFkMi+',
            description: 'From value is missing.',
          });
        }

        if (source === 'to') {
          return intl.formatMessage({
            defaultMessage: 'Please enter an end time.',
            id: 'tfHTZkRc7sr/Ypcl',
            description: 'End value is missing.',
          });
        }

        return intl.formatMessage({
          defaultMessage: 'Please enter a start and end time.',
          id: 'gKh7cp2la6bFgMqZ',
          description: 'Start and end value are missing.',
        });
      }
    },
    [expectedFormat, intl, max, min, precision],
  );

  return { getErrorMessage };
};
